@use 'src/globals/main';

pn-checkbox {
  position: relative;
  display: inline-block;
}

pn-checkbox .pn-checkbox {
  position: relative;
  display: flex;
  gap: 0.75em;

  &-label {
    color: main.$label-color;
  }

  &-helpertext {
    color: main.$helpertext-color;
  }

  &-content {
    display: flex;
    flex-direction: column;
    font-weight: 400;
    line-height: 1.5;
    flex: 1;
    margin: 0;
  }

  &-outer {
    width: 1.5em;
    height: 1.5em;
    background-color: main.$input-background;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    border: main.$input-border;
    border-radius: 0.25em;
    cursor: pointer;

    @include main.pn-focus-base();
    @include main.pn-transition('outline-color, border-color, background-color');

    svg {
      width: 1.25em;
      fill: none;

      polyline {
        stroke: main.$white;
        transform-origin: 0 0;
        stroke-linecap: round;
        stroke-dasharray: 23;
        stroke-dashoffset: 23;
        transition: stroke-dashoffset main.$timing main.$easing 0s;
      }
    }
  }

  &[data-tile] {
    position: relative;
    background-color: main.$input-background;
    border: main.$input-border;
    border-radius: main.$input-border-radius;
    padding: 1em;

    @include main.pn-focus-base();
    @include main.pn-transition('outline-color, background-color, border-color');

    &[data-invalid] {
      border-color: main.$border-color-error;
    }
  }

  &[data-invalid] .pn-checkbox-outer {
    border-color: main.$border-color-error;
  }
}

pn-checkbox > input {
  cursor: pointer;
  opacity: 0;
  position: absolute;
  margin: 0;
  z-index: 1;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  -webkit-tap-highlight-color: transparent;

  &[aria-describedby] + .pn-checkbox[data-tile] .pn-checkbox-label {
    font-weight: 500;
  }

  &:hover + .pn-checkbox {
    > .pn-checkbox-outer {
      background-color: main.$input-button-background-hover;
      border-color: main.$border-color-default-active;
    }
    &[data-tile] {
      background-color: main.$input-button-background-hover;
      border-color: main.$border-color-default-hover;
      &[data-invalid] {
        background-color: main.$input-background-error-hover;
        border-color: main.$border-color-error;
      }
    }
    &[data-invalid] > .pn-checkbox-outer {
      background-color: main.$input-background-error-hover;
      border-color: main.$border-color-error;
    }
  }

  &:focus-visible + .pn-checkbox {
    > .pn-checkbox-outer {
      @include main.pn-focus-visible(main.$border-color-default-focus);
    }
    &[data-tile] {
      @include main.pn-focus-visible(main.$border-color-default-focus, null, main.$border-color-default-focus);
      &[data-invalid] {
        @include main.pn-focus-visible(main.$border-color-error, null, main.$border-color-error);
        > .pn-checkbox-outer {
          @include main.pn-focus-visible(transparent);
        }
      }
      > .pn-checkbox-outer {
        @include main.pn-focus-visible(transparent);
      }
    }
    &[data-invalid] > .pn-checkbox-outer {
      @include main.pn-focus-visible(main.$border-color-error);
    }
  }

  &:checked {
    + .pn-checkbox {
      > .pn-checkbox-outer {
        background-color: main.$input-button-background-box-active;
        border-color: main.$border-color-default-active;

        svg .pn-checkbox-checkmark-path {
          transition-delay: main.$timing;
          stroke-dashoffset: 0;
        }
        svg .pn-checkbox-indeterminate-path {
          stroke-dashoffset: 23;
        }
      }

      &[data-tile] {
        background-color: main.$input-button-background-active;
        border-color: main.$border-color-default-active;
        &[data-invalid] {
          background-color: main.$input-background-error-active;
          border-color: main.$border-color-error-active;
        }
      }

      &[data-invalid] > .pn-checkbox-outer {
        background-color: main.$input-button-active-error;
        border-color: main.$border-color-error;
      }
    }

    &:hover + .pn-checkbox {
      &[data-tile] {
        border-color: main.$input-button-active-hover;
      }
      > .pn-checkbox-outer {
        background-color: main.$input-button-active-focus;
        border-color: main.$input-button-active-focus;
      }
      &[data-invalid] {
        border-color: main.$input-button-active-error-focus;
        > .pn-checkbox-outer {
          background-color: main.$input-button-active-error-focus;
          border-color: main.$input-button-active-error-focus;
        }
      }
    }
  }

  &:indeterminate {
    + .pn-checkbox {
      > .pn-checkbox-outer {
        background-color: main.$input-button-background-box-active;
        border-color: main.$input-button-background-box-active;
        svg .pn-checkbox-checkmark-path {
          stroke-dashoffset: 23;
        }
        svg .pn-checkbox-indeterminate-path {
          stroke-dashoffset: 0;
        }
      }

      &[data-invalid] {
        > .pn-checkbox-outer {
          background-color: main.$input-button-active-error;
          border-color: main.$border-color-error;
        }
      }
    }

    &:hover + .pn-checkbox {
      > .pn-checkbox-outer {
        background-color: main.$input-button-active;
        border-color: main.$input-button-active;
      }
      &[data-invalid] > .pn-checkbox-outer {
        background-color: main.$input-button-active-error-hover;
        border-color: main.$border-color-error-hover;
      }
    }
  }

  &:disabled {
    cursor: not-allowed;
    + .pn-checkbox {
      > .pn-checkbox-outer {
        background-color: main.$input-background-disabled;
        border-color: main.$border-color-default;
      }

      &[data-tile] {
        background-color: main.$input-background-disabled;
        border-color: main.$border-color-disabled;
        > pn-icon > .pn-icon-svg path {
          fill: main.$input-color-disabled;
        }
        > .pn-checkbox-content > .pn-checkbox-label {
          color: main.$input-color-disabled;
        }

        &[data-invalid] {
          background-color: main.$input-background-disabled;
          border-color: main.$border-color-disabled;
        }
      }
    }

    &:hover + .pn-checkbox {
      &[data-tile] {
        border-color: main.$border-color-disabled;
        &[data-invalid] {
          background-color: main.$input-background-disabled;
          border-color: main.$border-color-disabled;
        }
        > .pn-checkbox-outer {
          background-color: main.$input-background-disabled;
          border-color: main.$input-border-disabled;
        }
      }
      > .pn-checkbox-outer,
      &[data-invalid] > .pn-checkbox-outer {
        background-color: main.$input-background-disabled;
        border-color: main.$input-border-disabled;
      }
    }

    &:checked,
    &:checked:hover {
      + .pn-checkbox {
        > .pn-checkbox-outer,
        &[data-invalid] > .pn-checkbox-outer {
          background-color: main.$input-button-disabled;
          border-color: main.$input-border-disabled;
        }
      }
    }

    &:indeterminate,
    &:indeterminate:hover,
    &:indeterminate:disabled:hover {
      + .pn-checkbox > .pn-checkbox-outer {
        background-color: main.$input-button-disabled;
        border-color: main.$input-border-disabled;
      }
    }
  }
}
